import { CSP_NONCE, Inject, Injectable } from '@angular/core';
import { PlatformService } from '../universal/platform.service';

@Injectable({ providedIn: 'root' })
export class CustomCssService {
    // this now can handle nested {} blocks. needed for @media queries
    cssRegex = /([^{]+?)\s*(\{(?:[^}{]|\{(?:[^}{]|\{(?:[^}{]|\{[^}{]*\{)*\})*\})*\})/;
    cssCommentsRegex = /(\/\*.*?\*\/{1}?)/;
    cmsIdClassCache = new Set<string>();

    constructor(
        private platform: PlatformService,
        @Inject(CSP_NONCE) private cspNonce: string
    ) {}

    injectCss(customCssString: string, cmsIdClass?: string) {
        if (!customCssString) {
            return;
        }

        if (!cmsIdClass) {
            // do not scope to specific CMS component. just insert the custom CSS
            this.addStyleElement(customCssString);
            return;
        }

        if (this.cmsIdClassCache.has(cmsIdClass)) {
            // already added
            return;
        }

        if (this.platform.document.querySelector(`[data-cms-id='${cmsIdClass}']`)) {
            // if added already by SSR, just add it to cache
            this.cmsIdClassCache.add(cmsIdClass);

            return;
        }

        // Remove comments before further processing
        customCssString = customCssString.replace(RegExp(this.cssCommentsRegex, 'gs'), '');

        const finalCssString = this.processCustomCss(customCssString, cmsIdClass);

        this.addStyleElement(finalCssString, cmsIdClass);
        this.cmsIdClassCache.add(cmsIdClass);
    }

    addStyleElement(css: string, cmsIdClass?: string) {
        const headElement = this.platform.document.querySelector('head');
        const newStyleElement = this.platform.document.createElement('style');
        newStyleElement.nonce = this.cspNonce;
        if (cmsIdClass) {
            newStyleElement.setAttribute('data-cms-id', cmsIdClass);
        }

        headElement.appendChild(newStyleElement);
        if (newStyleElement['styleSheet']) {
            // IE
            newStyleElement['styleSheet'].cssText = css;
        } else {
            // the world
            newStyleElement.appendChild(this.platform.document.createTextNode(css));
        }
    }

    removeCustomStyleElement(cmsIdClass: string) {
        if (!cmsIdClass) {
            return;
        }

        const customStyleBlocks = this.platform.document.querySelectorAll(`[data-cms-id='${cmsIdClass}']`);
        customStyleBlocks.forEach(styleBlock => {
            styleBlock.remove();
        });
        this.cmsIdClassCache.delete(cmsIdClass);
    }

    private processCustomCss(customCssString: string, cmsIdClass: string) {
        const matcher = RegExp(this.cssRegex, 'g');

        let finalCssString = '';
        let cssRuleMatches = matcher.exec(customCssString);

        while (cssRuleMatches !== null) {
            finalCssString += this.processCssBlock(cssRuleMatches[1], cssRuleMatches[2], cmsIdClass);

            cssRuleMatches = matcher.exec(customCssString);
        }

        return finalCssString.trim();
    }

    private processCssBlock(selector: string, rulesBlock: string, cmsIdClass: string) {
        // scope styles to current cms component
        const customizedSelectorsForRule = selector
            .split(',')
            .map(s => s.trim())
            .map(s => {
                return s.startsWith(':root ') || s.startsWith('html ') || s.startsWith('@media')
                    ? s
                    : `.phx-body .${cmsIdClass} ${s !== ':host' ? s : ''}`;
            })
            .join(',\n');

        if (customizedSelectorsForRule.startsWith('@media')) {
            // process the nested css block
            const customCssBlock = this.processCustomCss(rulesBlock, cmsIdClass);
            return `${customizedSelectorsForRule} {\n ${customCssBlock}\n}\n`;
        }

        return `${customizedSelectorsForRule} ${rulesBlock}\n`;
    }
}
